import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_STYLES = {
  "Health Tip": "bg-brand-green/10 text-brand-greendark",
  "DermaGen Story": "bg-brand-blush text-brand-pinkdark",
  "Pharmacy News": "bg-muted text-muted-foreground",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetch(`${API}/posts`)
      .then((r) => r.json())
      .then(setPosts)
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section id="news" data-testid="news-section" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 lg:px-10">
      <div className="mb-14 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink" data-testid="news-eyebrow">News & Stories</p>
        <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-testid="news-heading">
          Health tips & stories from our team
        </h2>
        <p className="mt-4 text-base text-muted-foreground md:text-lg">
          Seasonal health advice from our pharmacists, plus the latest from DermaGen — straight from our compounding room.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
        {posts.map((p, i) => (
          <motion.article
            key={p.post_id}
            data-testid={`news-card-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
            className="group flex cursor-pointer flex-col rounded-2xl border border-border/60 bg-white p-7 shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(229,0,126,0.1)]"
            onClick={() => setActive(p)}
          >
            {p.image_path && (
              <img
                src={`${API}/files/${p.image_path}`}
                alt={p.title}
                data-testid={`news-image-${i}`}
                className="mb-5 h-40 w-full rounded-xl object-cover"
              />
            )}
            <div className="flex items-center justify-between gap-3">
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${CATEGORY_STYLES[p.category] || CATEGORY_STYLES["Pharmacy News"]}`} data-testid={`news-category-${i}`}>
                {p.category}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(p.created_at)}
              </span>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold leading-snug">{p.title}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
              {p.body.slice(0, 140)}{p.body.length > 140 ? "…" : ""}
            </p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-pink">
              Read more <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </motion.article>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="news-article-dialog" className="max-w-2xl rounded-3xl border-border/60">
          {active && (
            <div>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${CATEGORY_STYLES[active.category] || CATEGORY_STYLES["Pharmacy News"]}`}>
                {active.category}
              </span>
              <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight" data-testid="news-article-title">{active.title}</h3>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(active.created_at)} · Priceline Pharmacy Margaret River
              </p>
              {active.image_path && (
                <img
                  src={`${API}/files/${active.image_path}`}
                  alt={active.title}
                  data-testid="news-article-image"
                  className="mt-5 max-h-72 w-full rounded-2xl object-cover"
                />
              )}
              <div className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="news-article-body">
                {active.body}
              </div>
              <p className="mt-6 border-t border-border/60 pt-4 text-xs text-muted-foreground">
                Questions? Call us on (08) 9757 2224 or drop in — 146 Bussell Highway, Margaret River. Open 7 days, 8am–6pm.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
